import { useState } from 'react';
import { Button } from '@/components/ui/Button';
import { Textarea } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import { LoadingState, ErrorState } from '@/components/ui/States';
import { useAuth } from '@/hooks/useAuth';
import { useLabReport, useReleaseLabReport, useAmendLabReport } from './hooks';
import { openLabReportPdf } from './api';
import { AbnormalFlag, fmtDateTime, isCriticalFlag } from './ui';

/**
 * The report: a live preview of what would print, the release action, the
 * version history, and amendment.
 *
 * ── Two rules this panel makes visible ───────────────────────────
 *
 * The preview only ever shows VERIFIED results — the server assembles it from
 * verified rows and nothing else, so there is no view in this product where
 * an unverified number appears on something shaped like a report.
 *
 * A released version is never edited. Correcting one produces a NEW version;
 * the previous one keeps its own PDF, stays downloadable, and is marked
 * superseded. That is why the history below is a list rather than a single
 * "current" link — the copy a patient is holding has to stay reconstructable.
 */
export interface ReportPanelProps {
  orderId: number;
  uhid?: string;
}

export default function ReportPanel({ orderId, uhid }: ReportPanelProps) {
  const { user } = useAuth();
  const { data, isLoading, error, refetch } = useLabReport(orderId);
  const release = useReleaseLabReport();
  const amend = useAmendLabReport();
  const [amending, setAmending] = useState(false);
  const [reason, setReason] = useState('');
  const [actionError, setActionError] = useState<string | null>(null);

  const canRelease = !!user?.modules?.includes('lab:release');
  const canAmend = !!user?.modules?.includes('lab:amend');

  if (isLoading) return <LoadingState label="Loading the report…" />;
  if (error) return <ErrorState message={(error as Error).message} onRetry={() => refetch()} />;
  if (!data) return null;

  const { report, versions, preview } = data;
  const released = report && (report.status === 'released' || report.status === 'amended');
  const verifiedCount = preview?.tests.length ?? 0;

  const run = async (fn: () => Promise<unknown>) => {
    setActionError(null);
    try { await fn(); } catch (e) { setActionError((e as Error).message); }
  };

  return (
    <section className="rounded-xl border border-border p-3">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-semibold">
          Report
          {report && <span className="ml-2 font-mono text-[12px] text-muted-foreground">{report.report_number}</span>}
          {released && <Badge variant="success" className="ml-2">
            {report.status === 'amended' ? `Amended · v${report.current_version}` : 'Released'}
          </Badge>}
        </h3>
        <div className="flex gap-2">
          {!released && canRelease && (
            <Button
              size="sm"
              disabled={release.isPending || verifiedCount === 0}
              title={verifiedCount === 0 ? 'No verified results yet' : undefined}
              onClick={() => run(() => release.mutateAsync({ orderId, uhid }))}
            >
              {release.isPending ? 'Releasing…' : 'Release report'}
            </Button>
          )}
          {released && canAmend && !amending && (
            <Button variant="ghost" size="sm" onClick={() => setAmending(true)}>Publish an amendment</Button>
          )}
        </div>
      </div>

      {verifiedCount === 0 && (
        <p className="text-[12px] text-muted-foreground">
          Nothing to report yet — a report is assembled only from verified results, and none of this order’s
          tests have been verified.
        </p>
      )}

      {/* ── Preview ─────────────────────────────────────────────── */}
      {preview && verifiedCount > 0 && (
        <div className="overflow-x-auto rounded-lg border border-border">
          {preview.tests.map((t) => (
            <div key={t.order_item_id} className="border-b border-border last:border-0">
              <div className="bg-muted/40 px-3 py-1.5">
                <span className="text-[13px] font-semibold">{t.test_name}</span>
                <span className="ml-2 text-[11px] text-muted-foreground">
                  {[t.department_name, t.method ? `Method: ${t.method}` : null, t.sample_type_name].filter(Boolean).join(' · ')}
                </span>
              </div>
              <table className="w-full text-[12px]">
                <thead>
                  <tr className="text-[10px] uppercase tracking-wide text-muted-foreground">
                    <th className="px-3 py-1 text-left">Parameter</th>
                    <th className="px-3 py-1 text-right">Result</th>
                    <th className="px-3 py-1 text-left">Unit</th>
                    <th className="px-3 py-1 text-left">Reference</th>
                    <th className="px-3 py-1 text-left">Flag</th>
                  </tr>
                </thead>
                <tbody>
                  {t.values.map((v, i) => (
                    <tr key={i} className={isCriticalFlag(v.flag) ? 'bg-danger-soft/40' : ''}>
                      <td className="px-3 py-1">{v.parameter_name}</td>
                      <td className={`px-3 py-1 text-right font-mono ${v.flag !== 'NORMAL' && v.flag !== 'NOT_EVALUATED' ? 'font-semibold' : ''}`}>
                        {v.display_value}
                      </td>
                      <td className="px-3 py-1 text-muted-foreground">{v.unit ?? ''}</td>
                      <td className="px-3 py-1 text-muted-foreground">{v.reference_display ?? ''}</td>
                      <td className="px-3 py-1"><AbnormalFlag flag={v.flag} compact /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {(t.technician_comments || t.verification_comments) && (
                <div className="px-3 pb-1.5 text-[11px] italic text-muted-foreground">
                  {t.technician_comments && <div>Technical note: {t.technician_comments}</div>}
                  {t.verification_comments && <div>Interpretation: {t.verification_comments}</div>}
                </div>
              )}
              <div className="px-3 pb-2 text-[11px] text-muted-foreground">
                Verified by {t.verified_by ?? '—'} · {fmtDateTime(t.verified_at)}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Amendment ───────────────────────────────────────────── */}
      {amending && (
        <div className="mt-3 space-y-2 rounded-lg border border-border p-3">
          <p className="text-[12px] text-muted-foreground">
            This publishes a new version of the report. The current one is kept exactly as released and marked
            superseded — nothing is overwritten. The reason you give is printed on the amended report.
          </p>
          <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)}
            placeholder="Why is this report being amended? (required)" />
          {actionError && <ErrorState message={actionError} />}
          <div className="flex gap-2">
            <Button size="sm" disabled={!reason.trim() || amend.isPending}
              onClick={() => run(async () => {
                await amend.mutateAsync({ orderId, reason: reason.trim(), uhid });
                setAmending(false); setReason('');
              })}>
              {amend.isPending ? 'Publishing…' : 'Publish amendment'}
            </Button>
            <Button variant="ghost" size="sm" onClick={() => { setAmending(false); setReason(''); }}>Cancel</Button>
          </div>
        </div>
      )}

      {actionError && !amending && <ErrorState className="mt-2" message={actionError} />}

      {/* ── Version history ─────────────────────────────────────── */}
      {versions.length > 0 && (
        <div className="mt-3">
          <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            Version history
          </div>
          <div className="divide-y divide-border rounded-lg border border-border">
            {versions.map((v) => (
              <div key={v.id} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-[12px]">
                <div>
                  <span className="font-semibold">Version {v.version}</span>
                  {v.superseded_at
                    ? <Badge variant="neutral" className="ml-2">Superseded</Badge>
                    : <Badge variant="success" className="ml-2">Current</Badge>}
                  <div className="text-muted-foreground">
                    Released {fmtDateTime(v.released_at)} by {v.released_by ?? '—'}
                  </div>
                  {v.amendment_reason && (
                    <div className="text-warn-ink">Amendment: {v.amendment_reason}</div>
                  )}
                </div>
                <Button variant="ghost" size="sm" onClick={() => openLabReportPdf(v.id).catch((e) => setActionError((e as Error).message))}>
                  Open PDF
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}
